/**
 * Client-side validation utilities for structured tags
 */

/**
 * Validate a text input value against its configuration
 */
export function validateText(value, config) {
    const errors = []

    if (config.max_length && value.length > config.max_length) {
        errors.push(`Massimo ${config.max_length} caratteri`)
    }

    if (config.pattern) {
        const regex = new RegExp(config.pattern)
        if (!regex.test(value)) {
            errors.push('Formato non valido')
        }
    }

    return errors
}

/**
 * Validate a number input value against its configuration
 */
export function validateNumber(value, config) {
    const errors = []
    const numValue = Number(value)

    if (isNaN(numValue)) {
        errors.push('Deve essere un numero')
        return errors
    }

    if (config.min_value !== null && config.min_value !== undefined && numValue < config.min_value) {
        errors.push(`Valore minimo: ${config.min_value}`)
    }

    if (config.max_value !== null && config.max_value !== undefined && numValue > config.max_value) {
        errors.push(`Valore massimo: ${config.max_value}`)
    }

    return errors
}

/**
 * Validate a select input value against its configuration (single value only)
 */
export function validateSelect(value, config) {
    const validValues = config.options.map(opt => opt.value)

    if (!validValues.includes(value)) {
        return [`Valore non valido. Valori ammessi: ${validValues.join(', ')}`]
    }

    return []
}

/**
 * Validate a multiselect input value against its configuration (always array)
 */
export function validateMultiselect(values, config) {
    const validValues = config.options.map(opt => opt.value)
    const valueArray = Array.isArray(values) ? values : [values]

    for (const value of valueArray) {
        if (!validValues.includes(value)) {
            return [`Valore non valido: ${value}. Valori ammessi: ${validValues.join(', ')}`]
        }
    }

    return []
}

/**
 * Validate checkbox values against configuration
 */
export function validateCheckbox(values, config) {
    const validValues = config.options.map(opt => opt.value)
    const valueArray = Array.isArray(values) ? values : [values]

    for (const value of valueArray) {
        if (!validValues.includes(value)) {
            return [`Valore non valido: ${value}`]
        }
    }

    return []
}

/**
 * Validate a date input value against its configuration
 */
export function validateDate(value, config) {
    const errors = []
    const date = new Date(value)

    if (isNaN(date.getTime())) {
        errors.push('Data non valida')
        return errors
    }

    if (config.min_date) {
        const minDate = new Date(config.min_date)
        if (date < minDate) {
            errors.push(`Data minima: ${config.min_date}`)
        }
    }

    if (config.max_date) {
        const maxDate = new Date(config.max_date)
        if (date > maxDate) {
            errors.push(`Data massima: ${config.max_date}`)
        }
    }

    return errors
}

/**
 * Validate tags array input value
 * For 'tags' type - free-form array of strings with suggestions
 */
export function validateTagsArray(values, config) {
    const errors = []

    // Must be an array
    if (!Array.isArray(values)) {
        errors.push('Deve essere un array di valori')
        return errors
    }

    // Check each value is a string
    for (const value of values) {
        if (typeof value !== 'string') {
            errors.push('Tutti i valori devono essere stringhe')
            break
        }

        // Check for empty strings
        if (value.trim() === '') {
            errors.push('I valori non possono essere vuoti')
            break
        }
    }

    // Check max_tags if configured
    if (config.max_tags && values.length > config.max_tags) {
        errors.push(`Massimo ${config.max_tags} tag`)
    }

    return errors
}

/**
 * Validate a tag value based on its definition
 */
export function validateTagValue(value, definition) {
    if (!value && value !== 0 && value !== false) {
        return [] // Empty values are allowed (not required by default)
    }

    const config = definition.input_config

    switch (definition.input_type) {
        case 'text':
            return validateText(value, config)
        case 'number':
            return validateNumber(value, config)
        case 'select':
            return validateSelect(value, config)
        case 'multiselect':
            return validateMultiselect(value, config)
        case 'checkbox':
            return validateCheckbox(value, config)
        case 'date':
            return validateDate(value, config)
        case 'tags':
            return validateTagsArray(value, config)
        default:
            return []
    }
}

/**
 * Validate all tags in a structured tags object
 * @param {Object} tags - Object with code as key and value
 * @param {Array} definitions - Array of tag definitions
 * @returns {Object} Object with code as key and array of error messages
 */
export function validateTags(tags, definitions) {
    const errors = {}

    for (const [code, value] of Object.entries(tags)) {
        const definition = definitions.find(def => def.code === code)

        if (!definition) {
            errors[code] = ['Tag non trovato nelle definizioni']
            continue
        }

        const fieldErrors = validateTagValue(value, definition)
        if (fieldErrors.length > 0) {
            errors[code] = fieldErrors
        }
    }

    return errors
}

/**
 * Check if there are any validation errors
 */
export function hasErrors(errors) {
    return Object.keys(errors).length > 0
}

/**
 * Convert structured tags object to API format
 * Ensures proper type casting for numbers, dates, etc.
 */
export function formatTagsForApi(tags, definitions) {
    const formatted = {}

    for (const [code, value] of Object.entries(tags)) {
        const definition = definitions.find(def => def.code === code)

        if (!definition) continue

        // Skip empty values
        if (value === '' || value === null || value === undefined) continue

        switch (definition.input_type) {
            case 'number':
                formatted[code] = Number(value)
                break
            case 'multiselect':
            case 'checkbox':
                // Always ensure it's an array
                formatted[code] = Array.isArray(value) ? value : [value]
                break
            case 'date':
                // Ensure ISO format
                formatted[code] = value instanceof Date ? value.toISOString().split('T')[0] : value
                break
            case 'tags':
                // Ensure it's an array (backend will normalize values)
                formatted[code] = Array.isArray(value) ? value : [value]
                break
            default:
                formatted[code] = value
        }
    }

    return formatted
}
